import { router, useForm, usePage } from "@inertiajs/vue3";
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { toast } from "vue-sonner";

import type {
  CreatorPageProps,
  EditorPageProps,
  FieldConfig,
  SharedCrudData,
  TabConfig
} from "../types";
import { destroy, index, store, update } from "../wayfinder/routes";

import { getNestedValue, setNestedValue } from "./useFieldVisibility";

type FormPageProps = EditorPageProps | CreatorPageProps;

/**
 * Composable for Inertia form state management.
 * Handles form submission, validation, and field management.
 */
export function useCrudForm(props: FormPageProps) {
  const { t } = useI18n();
  const page = usePage();
  const crud = computed(() => (page.props as any).crud as SharedCrudData);

  const isEdit = "id" in props;

  // Fallback to shared data if not in props
  const activeModel = computed(
    () => props.model ?? crud.value?.active?.model ?? ""
  );
  const activeConfig = computed(() => props.config ?? crud.value?.active);

  /**
   * Inertia's useForm has a .data() method that conflicts with form fields named "data".
   * We use this prefix internally to avoid the collision and transform back on submit.
   */
  const DATA_FIELD_PREFIX = "__data__";

  /** Transform field key to avoid Inertia's .data() collision */
  function toSafeKey(key: string): string {
    if (key === "data") return DATA_FIELD_PREFIX;
    if (key.startsWith("data.")) return DATA_FIELD_PREFIX + key.slice(4);
    return key;
  }

  /** Transform form data object: replace __data__ keys with data keys */
  function transformForSubmit(
    formData: Record<string, unknown>
  ): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(formData)) {
      if (key === DATA_FIELD_PREFIX) {
        result["data"] = value;
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  /** Build initial form data from fields and defaults/item */
  function buildFormData(): Record<string, unknown> {
    const data: Record<string, unknown> = {};
    const fields = getFields();
    const source = isEdit
      ? (props as EditorPageProps).item
      : (props as CreatorPageProps).defaults;

    fields.forEach((field) => {
      // Use getNestedValue to support dot notation (e.g., 'data.leave_type')
      const value = getNestedValue(
        source as Record<string, unknown>,
        field.key
      );
      // Transform "data" fields to safe keys to avoid Inertia's .data() collision
      const safeKey = toSafeKey(field.key);
      // Store using setNestedValue to maintain nested structure
      setNestedValue(data, safeKey, value ?? field.default ?? null);
    });

    const sourceTags = (source as Record<string, unknown>)?.__crud_tags;
    if (Array.isArray(sourceTags)) {
      // Editor payload provides objects, creator defaults may provide ids.
      data.__crud_tags = sourceTags
        .map((entry) => {
          if (typeof entry === "string") return entry;
          if (entry && typeof entry === "object" && "id" in entry) {
            const id = (entry as { id?: unknown }).id;
            return typeof id === "string" ? id : "";
          }
          return "";
        })
        .filter((id) => id !== "");
    } else {
      data.__crud_tags = [];
    }

    // For creator mode with parent context, include the parent foreign key
    if (!isEdit && props.parentContext) {
      const { foreignKey, id } = props.parentContext;
      data[foreignKey] = id;
    }

    return data;
  }

  /** Get all fields (from tabs/blocks for editor, from fields for creator) */
  function getFields(): FieldConfig[] {
    if (!activeConfig.value) return [];

    if (isEdit) {
      const editorConfig = activeConfig.value.editor;
      const fields = editorConfig.tabs.flatMap((tab: TabConfig) => {
        // Get direct tab fields
        const tabFields = tab.fields ?? [];
        // Get fields from blocks
        const blockFields = (tab.blocks ?? []).flatMap((block) => {
          // Regular blocks with fields
          const fields = block.fields ?? [];
          // Custom blocks (components) - add their key as a field
          if (block.component && block.key) {
            // Check if componentProps specifies a fieldKey
            const fieldKey = (block.componentProps as any)?.fieldKey ?? block.key;
            return [
              ...fields,
              {
                key: fieldKey,
                type: 'custom',
                required: false,
              } as FieldConfig,
            ];
          }
          return fields;
        });
        return [...tabFields, ...blockFields];
      });

      return [
        ...fields,
        {
          key: "__crud_tags",
          type: "custom",
          required: false,
        } as FieldConfig,
      ];
    }
    const creatorConfig = activeConfig.value.creator;
    const directFields = creatorConfig.fields ?? [];
    const blockFields = (creatorConfig.blocks ?? []).flatMap((block) => {
      const fields = block.fields ?? [];
      // Custom blocks (components) - add their key as a field
      if (block.component && block.key) {
        const fieldKey = (block.componentProps as any)?.fieldKey ?? block.key;
        return [
          ...fields,
          {
            key: fieldKey,
            type: 'custom',
            required: false,
          } as FieldConfig,
        ];
      }
      return fields;
    });
    return [
      ...directFields,
      ...blockFields,
      {
        key: "__crud_tags",
        type: "custom",
        required: false,
      } as FieldConfig,
    ];
  }

  /** Create Inertia form */
  const initialData = buildFormData();
  // Keep a mutable copy for tracking baseline (used in getDirtyFields)
  const baselineData = { ...initialData };
  const form = useForm(initialData as any);

  // Apply transform to convert __data__ back to data on submission
  form.transform((data: Record<string, unknown>) => transformForSubmit(data));

  /**
   * Reseed form values, baseline, and Inertia defaults from the current
   * `props.item` (edit mode). Without this the Inertia patch updates
   * `props.item` while `useForm` keeps its own copy — the Switch's
   * `switchChecked` getter then reads the stale form copy and visually
   * flips back even though the DB persisted the new value.
   */
  function resyncFromItem(): void {
    if (!isEdit) return;
    const fresh = buildFormData();
    for (const [key, value] of Object.entries(fresh)) {
      (form as any)[key] = value;
      setNestedValue(baselineData, key, value);
    }
    form.defaults(form.data() as any);
  }

  /** Submit form */
  function submit(currentTab?: string) {
    if (!activeModel.value) return;

    if (isEdit) {
      const { id } = props as EditorPageProps;
      // Build URL with tab query param to preserve current tab on redirect
      let url = update({ model: activeModel.value, id }).url;
      if (currentTab) {
        url += `?tab=${encodeURIComponent(currentTab)}`;
      }
      form.patch(url, {
        preserveScroll: true,
        onSuccess: () => {
          resyncFromItem();
          toast.success(t("crud.toast.updated"));
        },
        onError: () => toast.error(t("crud.toast.update_error"))
      });
    } else {
      form.post(store({ model: activeModel.value }).url, {
        preserveScroll: true,
        onSuccess: () => toast.success(t("crud.toast.created")),
        onError: () => toast.error(t("crud.toast.create_error"))
      });
    }
  }

  if (isEdit) {
    // Catch external `props.item` swaps (e.g. partial reloads) that the
    // submit `onSuccess` path won't see.
    watch(
      () => (props as EditorPageProps).item,
      () => {
        if (form.processing) return;
        resyncFromItem();
      }
    );
  }

  /** Reset form to initial state */
  function reset() {
    form.reset();
  }

  /** Delete item (editor only) */
  function destroyItem() {
    if (!isEdit || !activeModel.value) return;
    const { id } = props as EditorPageProps;
    router.delete(destroy({ model: activeModel.value, id }).url, {
      onSuccess: () => toast.success(t("crud.toast.deleted")),
      onError: () => toast.error(t("crud.toast.delete_error"))
    });
  }

  /** Navigate back to lister */
  function cancel() {
    if (!activeModel.value) return;
    router.get(index({ model: activeModel.value }).url);
  }

  /** Get field value from form (handles data field transformation) */
  function getFieldValue(key: string): unknown {
    const safeKey = toSafeKey(key);
    return getNestedValue(form as unknown as Record<string, unknown>, safeKey);
  }

  /** Set field value in form (handles data field transformation) */
  function setFieldValue(key: string, value: unknown): void {
    const safeKey = toSafeKey(key);
    // Use direct property assignment for non-nested keys to ensure Vue reactivity
    if (!safeKey.includes('.')) {
      // Direct assignment is atomic and preserves reactivity in Vue 3
       
      (form as any)[safeKey] = value;
    } else {
      setNestedValue(form as unknown as Record<string, unknown>, safeKey, value);
    }
  }

  /** Check if field has error */
  function hasError(key: string): boolean {
    // Errors come back from server with original keys (data.*)
    return !!form.errors[key as keyof typeof form.errors];
  }

  /** Get field error message */
  function getError(key: string): string | undefined {
    // Errors come back from server with original keys (data.*)
    return form.errors[key as keyof typeof form.errors] as string | undefined;
  }

  /** Computed properties */
  const isDirty = computed(() => form.isDirty);
  const isProcessing = computed(() => form.processing);
  const hasErrors = computed(() => Object.keys(form.errors).length > 0);

  /**
   * Get list of fields that have been modified from their baseline values.
   * Used for conflict detection in real-time updates.
   * Compares against baselineData which is updated when server data is merged.
   */
  function getDirtyFields(): string[] {
    const dirty: string[] = [];
    const fields = getFields();

    for (const field of fields) {
      const safeKey = toSafeKey(field.key);
      const currentValue = getNestedValue(
        form as unknown as Record<string, unknown>,
        safeKey,
      );
      const baselineValue = getNestedValue(baselineData, safeKey);

      // Deep comparison for objects/arrays, simple comparison for primitives
      const isChanged = JSON.stringify(currentValue) !== JSON.stringify(baselineValue);
      if (isChanged) {
        // Return the original key (not the safe key)
        dirty.push(field.key);
      }
    }

    return dirty;
  }

  /**
   * Sync Inertia's form defaults to baselineData so form.isDirty stays accurate.
   *
   * Why: form.defaults(form.data()) would rebase against the *current* form
   * values, which include local-only edits to fields the server did not touch.
   * That silently clears form.isDirty for those untouched-by-server fields and
   * the user loses the visual signal (and Save button) that they still have
   * pending edits. Defaulting to the baseline preserves dirty tracking for any
   * field whose current value diverges from baseline.
   */
  function syncDefaultsToBaseline(): void {
    form.defaults(JSON.parse(JSON.stringify(baselineData)) as any);
  }

  /**
   * Merge server data into the form for non-conflicting fields.
   * Only updates fields that the user hasn't modified.
   * Also updates the baseline so future dirty checks and reset work correctly.
   */
  function mergeServerData(serverData: Record<string, unknown>): void {
    const dirtyFields = getDirtyFields();

    for (const [key, value] of Object.entries(serverData)) {
      if (dirtyFields.includes(key)) continue;

      setFieldValue(key, value);

      const safeKey = toSafeKey(key);
      setNestedValue(baselineData, safeKey, value);
    }

    syncDefaultsToBaseline();
  }

  /**
   * Accept server data for the conflicting fields the server delivered.
   * Used when user chooses "Accept Theirs" in conflict resolution.
   *
   * Local edits to fields the server did NOT include in serverData are kept
   * AND remain flagged dirty (see syncDefaultsToBaseline) so the user still
   * has a Save button and a clear signal that they hold uncommitted state.
   */
  function acceptServerData(serverData: Record<string, unknown>): void {
    for (const [key, value] of Object.entries(serverData)) {
      const safeKey = toSafeKey(key);
      setFieldValue(key, value);
      setNestedValue(baselineData, safeKey, value);
    }

    syncDefaultsToBaseline();
  }

  return {
    // Form instance
    form,
    isEdit,
    activeModel,
    activeConfig,

    // Helpers
    getFields,
    getFieldValue,
    setFieldValue,
    toSafeKey,

    // Actions
    submit,
    reset,
    destroy: destroyItem,
    cancel,

    // Validation
    hasError,
    getError,

    // State
    isDirty,
    isProcessing,
    hasErrors,

    // Real-time helpers
    getDirtyFields,
    mergeServerData,
    acceptServerData,
  };
}
